import { useCallback, useEffect, useRef, useState } from 'react';
import type { Email } from '@/types/email';
import type { AiAnnotation } from '@/types/ai';
import { fetchMessageSummary, runMessageSummary } from '@/api/aiTasksApi';
import { ApiError } from '@/api/core/client';

export interface UseMessageSummaryResult {
  annotation: AiAnnotation | null;
  quickSummary: string | null;
  isLoading: boolean;
  error: string | null;
  isSupported: boolean;
  requestSummary: (options?: { force?: boolean }) => Promise<AiAnnotation | null>;
  resetError: () => void;
}

export function useMessageSummary(email: Email | null): UseMessageSummaryResult {
  // aiSummary from email may be a partial AiSummary type; cast for compatibility
  const [annotation, setAnnotation] = useState<AiAnnotation | null>((email?.aiSummary as AiAnnotation | null) ?? null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setSupported] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const emailId = email?.id ?? null;
  const emailAccountId = email?.accountId ?? null;
  const emailSummary = email?.aiSummary ?? null;
  const quickSummary = email?.aiQuickSummary ?? null;

  // Identity anchor for the EXPLICIT request path (the passive fetch effect has
  // its own `cancelled` guard): a summary requested for message A must never
  // render under message B after navigation (LLM latency makes this common).
  const currentEmailIdRef = useRef<string | null>(emailId);

  useEffect(() => {
    currentEmailIdRef.current = emailId;
    setAnnotation(emailSummary as AiAnnotation | null);
    setError(null);
    // Loading must reset with the email (N11): the previous email's guarded
    // finally skips setLoading(false) after navigation, and an email whose
    // quick summary skips the passive fetch would otherwise spin forever —
    // hiding that quick summary and disabling Generate.
    setLoading(false);
    setHasFetched(Boolean(emailSummary));
  }, [emailId, emailSummary]);

  useEffect(() => {
    // Skip fetch if we already have a summary (full annotation or quick summary) from the email object
    if (!emailId || !emailAccountId || !isSupported || hasFetched || emailSummary || quickSummary) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchMessageSummary({ accountId: emailAccountId, messageId: emailId })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result) {
          setAnnotation(result);
        }
      })
    .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError && err.status === 503) {
          setSupported(false);
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load summary');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setHasFetched(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [emailAccountId, emailId, isSupported, hasFetched, emailSummary, quickSummary]);

  const requestSummary = useCallback(
    async (options?: { force?: boolean }) => {
      if (!emailAccountId || !emailId) {
        return null;
      }
      const { force = false } = options || {};
      const requestEmailId = emailId;
      setError(null);
      setSupported(true);
      setLoading(true);
      try {
        const result = await runMessageSummary({
          accountId: emailAccountId,
          messageId: emailId,
          force,
          source: 'detail_view',
        });
        // Drop the response silently if the user navigated away mid-request.
        if (currentEmailIdRef.current !== requestEmailId) {
          return null;
        }
        setAnnotation(result);
        setHasFetched(true);
        return result;
      } catch (err) {
        if (currentEmailIdRef.current !== requestEmailId) {
          return null;
        }
        if (err instanceof ApiError && err.status === 503) {
          setSupported(false);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to run summary');
        }
        throw err;
      } finally {
        if (currentEmailIdRef.current === requestEmailId) {
          setLoading(false);
        }
      }
    },
    [emailAccountId, emailId],
  );

  const resetError = useCallback(() => setError(null), []);

  return {
    annotation,
    quickSummary,
    isLoading,
    error,
    isSupported,
    requestSummary,
    resetError,
  };
}
